// ============================================================================
// Piezas compartidas de la app «Documentación»: estado de una consulta para el
// LoadBlock de la consola, chips de estado del expediente y celda del embarque
// (enlaza al expediente dentro de la app).
// ============================================================================
import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, cn } from "../../../components/ui";
import { documentsErrorMessage, type DocumentSubjectRef, type SubjectState } from "../../../services/shipmentDocuments";
import { DOCS_NS, KIND_ICONS, STATE_ICONS, STATE_TONES } from "../../../features/shipmentDocuments/meta";
import { documentacionSubjectRoute } from "../nav";

/** Estado para `LoadBlock` (consola) a partir de una consulta de react-query. */
export function queryStatus(q: Pick<UseQueryResult, "isLoading" | "isError" | "error" | "refetch">): { status: "loading" | "error" | "ready"; error: string | null; retry: () => void } {
  return {
    status: q.isLoading ? "loading" : q.isError ? "error" : "ready",
    error: q.isError ? documentsErrorMessage(q.error, "") : null,
    retry: () => void q.refetch(),
  };
}

export function StateBadge({ state, size = "sm" }: { state: SubjectState; size?: "sm" | "md" }) {
  const { t } = useTranslation(DOCS_NS);
  return (
    <span role="status" aria-label={`${t("expedientes.columns.state")}: ${t(`state.${state}`)}`} className="inline-flex">
      <Badge size={size} tone={STATE_TONES[state]} dot>
        <i className={STATE_ICONS[state]} /> {t(`state.${state}`)}
      </Badge>
    </span>
  );
}

export function KindBadge({ kind }: { kind: DocumentSubjectRef["kind"] }) {
  const { t } = useTranslation(DOCS_NS);
  return (
    <Badge size="sm" tone="neutral">
      <i className={KIND_ICONS[kind]} /> {t(`kind.${kind}`)}
    </Badge>
  );
}

/** Nombre corto del embarque: etiqueta o id. */
export function subjectLabel(s: { label?: string | null; reference?: string | null; id: string }): string {
  return s.label || s.reference || s.id;
}

export function SubjectCell({ subject, label, secondary, className }: { subject: DocumentSubjectRef; label?: string | null; secondary?: string | null; className?: string }) {
  const { t } = useTranslation(DOCS_NS);
  return (
    <Link to={documentacionSubjectRoute(subject.kind, subject.id)} onClick={(e) => e.stopPropagation()} className={cn("group inline-flex min-w-0 items-center gap-2 text-left", className)} title={t("expedientes.detailTitle")}>
      <i className={cn(KIND_ICONS[subject.kind], "shrink-0 text-content-muted")} aria-label={t(`kind.${subject.kind}`)} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-content group-hover:text-primary-500">{label || subject.id}</span>
        {secondary ? <span className="block truncate text-[11px] text-content-muted">{secondary}</span> : null}
      </span>
    </Link>
  );
}

/** Ruta de la app de origen del sujeto (envío por su guía, reserva en su lista) para «Abrir en su app». */
export function subjectAppRoute(subject: DocumentSubjectRef & { reference?: string | null }): string {
  return subject.kind === "shipment" ? `/envios/detalle/${encodeURIComponent(subject.reference || subject.id)}` : "/cargo-market/aerea-maritima/reservas";
}
